import { PropTypes } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, formValueSelector, reduxForm, change } from 'redux-form';
import Permission, { RoleTypes } from '../../../core/components/Permission';
import { fieldRequired, requiredSelect } from '../../../core/formValidation';
import Loader from '../../../core/components/Loader';

export const UpdateSettingsForm = ({
  initialValues,
  siteModelYears,
  siteOperationTypes,
  formatMessage,
  role,
  isLoading,
  isUpdating,
  dispatch,
}) => {
  const siteSettings = initialValues;
  const siteModelYearOptions = siteModelYears.map((modelYear) => ({
    text: modelYear.year,
    value: modelYear.id,
  }));
  siteModelYearOptions.unshift({ text: '', value: null });
  const operationTypeOptions = siteOperationTypes.map((operation) => ({
    text: operation.name,
    value: operation.id,
  }));
  operationTypeOptions.unshift({ text: '', value: null });
  const tunnelLengthOptions = [
    { text: 'None', value: null },
    { text: '50ft', value: 50 },
    { text: '55ft', value: 55 },
    { text: '80ft', value: 80 },
    { text: '90ft', value: 90 },
    { text: '100ft', value: 100 },
    { text: '110ft', value: 110 },
    { text: '130ft', value: 130 },
    { text: '140ft', value: 140 },
    { text: '150ft', value: 150 },
    { text: '180ft', value: 180 },
  ];

  const handleWaterChange = (event) => {
    if (event.target.checked) {
      dispatch(change('settings', 'activeUtilities.WaterSewer', false));
    }
  };
  const handleSewerChange = (event) => {
    if (event.target.checked) {
      dispatch(change('settings', 'activeUtilities.WaterSewer', false));
    }
  };
  const handleWaterSewerChange = (event) => {
    if (event.target.checked) {
      dispatch(change('settings', 'activeUtilities.Water', false));
      dispatch(change('settings', 'activeUtilities.Sewer', false));
    }
  };

  return isLoading || isUpdating ? (
    <Loader />
  ) : (
    <div data-testid="updateSettingsForm" className="settings-form">
      <div className="form-group">
        <div data-testid="disableWorkLogs">
          <label className="form-check-label">
            <Field
              name="workLogsDisabled"
              component="input"
              className="form-check-input"
              type="checkbox"
            />{' '}
            {formatMessage({ id: 'disableWorkLogs' })}
          </label>
        </div>
        <div data-testid="disableSiteManagerEditing">
          <label className="form-check-label">
            <Field
              name="managerAccessDisabled"
              component="input"
              className="form-check-input"
              type="checkbox"
            />{' '}
            {formatMessage({ id: 'disableSiteManagerEditing' })}
          </label>
        </div>
        <div data-testid="enableWashCounter">
          <label className="form-check-label">
            <Field
              name="washCounterEnabled"
              component="input"
              value={siteSettings?.washCountEnabled || false}
              className="form-check-input"
              type="checkbox"
            />{' '}
            {formatMessage({ id: 'enableWashCounter' })}
          </label>
        </div>
        <div data-testid="enableVpnGroup">
          <label className="form-check-label">
            <Field
              name="vpnGroupEnabled"
              component="input"
              value={siteSettings?.vpnGroupEnabled || false}
              className="form-check-input"
              type="checkbox"
            />{' '}
            {formatMessage({ id: 'useDashboardVpnSettings' })}
          </label>
        </div>
        <div>
          <label className="form-check-label">
            <Field
              name="dateRangeLimiterEnabled"
              component="input"
              value={siteSettings?.dateRangeLimiterEnabled || false}
              className="form-check-input"
              type="checkbox"
            />{' '}
            {formatMessage({ id: 'dateRangeLimiterEnabled' })}
          </label>
        </div>
      </div>
      {/* create a new dropdown field for site length */}
      <div className="form-group" style={{ marginBottom: '.5rem' }}>
        <label className="form-check-label">
          {formatMessage({ id: 'tunnelLength' })}
          <Field
            id="tunnelLength"
            name="tunnelLength"
            component={requiredSelect}
            options={tunnelLengthOptions}
            parse={(value) => value && Number(value)}
            validate={[fieldRequired]}
            onBlur={(e) => {
              e.preventDefault();
            }}
          />
        </label>
      </div>
      <Permission role={role} minRole={RoleTypes.OperatorGroupAdmin}>
        <div className="form-row">
          <div className="form-group half">
            <label className="form-check-label">
              {formatMessage({ id: 'siteOperationTypes' })}
              <Field
                id="operationTypeId"
                name="operationTypeId"
                data-testid="operationTypeId"
                component={requiredSelect}
                options={operationTypeOptions}
                parse={(value) => value && Number(value)}
                validate={[fieldRequired]}
                onBlur={(e) => {
                  e.preventDefault();
                }}
              />
            </label>
          </div>
          <div className="form-group half">
            <label className="form-check-label">
              {formatMessage({ id: 'siteModelYears' })}
              <Field
                id="modelYearId"
                name="modelYearId"
                data-testid="modelYearId"
                component={requiredSelect}
                options={siteModelYearOptions}
                parse={(value) => value && Number(value)}
                validate={[fieldRequired]}
                onBlur={(e) => {
                  e.preventDefault();
                }}
              />
            </label>
          </div>
        </div>
      </Permission>
      <div className="form-group utilities-options">
        <h3>Utility Tracking</h3>
        <div>
          <label className="no-select">
            <Field
              name="activeUtilities.Water"
              component="input"
              type="checkbox"
              onClick={handleWaterChange}
            />{' '}
            {formatMessage({ id: 'water' })}
          </label>
        </div>
        <div>
          <label className="no-select">
            <Field name="activeUtilities.Electric" component="input" type="checkbox" />{' '}
            {formatMessage({ id: 'electric' })}
          </label>
        </div>
        <div>
          <label className="no-select">
            <Field name="activeUtilities.Gas" component="input" type="checkbox" />{' '}
            {formatMessage({ id: 'gas' })}
          </label>
        </div>
        <div>
          <label className="no-select">
            <Field
              name="activeUtilities.Sewer"
              component="input"
              type="checkbox"
              onClick={handleSewerChange}
            />{' '}
            {formatMessage({ id: 'sewer' })}
          </label>
        </div>
        <div>
          <label className="no-select">
            <Field
              name="activeUtilities.WaterSewer"
              component="input"
              type="checkbox"
              onClick={handleWaterSewerChange}
            />{' '}
            {formatMessage({ id: 'water' })} / {formatMessage({ id: 'sewer' })}
          </label>
        </div>
      </div>
    </div>
  );
};

UpdateSettingsForm.propTypes = {
  modelYearId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  operationTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formatMessage: PropTypes.func,
  handleSubmit: PropTypes.func,
  siteOperationTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  siteModelYears: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      year: PropTypes.string,
    })
  ),
  role: PropTypes.string.isRequired,
  activeUtilities: PropTypes.shape({
    trackWater: PropTypes.bool,
    trackElectric: PropTypes.bool,
    trackGas: PropTypes.bool,
    trackSewer: PropTypes.bool,
    trackWaterSewer: PropTypes.bool,
  }),
  dispatch: PropTypes.func.isRequired,
};

const selector = formValueSelector('settings');
const enhance = compose(
  connect(
    (state) => ({
      modelYearId: selector(state, 'modelYearId'),
      operationTypeId: selector(state, 'operationTypeId'),
    }),
    null
  ),
  reduxForm({ form: 'settings', enableReinitialize: true })
);

export default enhance(UpdateSettingsForm);
